import React, { useEffect } from 'react';
import { playSelectSound } from '../utils/sound';

interface HowToPlayProps {
    isOpen: boolean;
    onClose: () => void;
}

const HowToPlay: React.FC<HowToPlayProps> = ({ isOpen, onClose }) => {
    // Prevent body scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleClose = () => {
        playSelectSound();
        onClose();
    };

    return (
        <div className="pirate-dictionary-overlay" onClick={(e) => {
            // Close when clicking the overlay background
            if (e.target === e.currentTarget) {
                handleClose();
            }
        }}>
            <div className="pirate-dictionary-popup">
                <div className="popup-header">
                    <h3 className="nes-text is-warning">How to Play</h3>
                    <button
                        className="nes-btn is-error close-button"
                        onClick={handleClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <div className="dictionary-content">
                    <div className="how-to-play-content">
                        <p><span className="nes-text is-primary">Objective:</span>
                            Defeat your opponent in a battle of wits and words! Take turns exchanging
                            creative insults and clever comebacks to reduce your opponent's health.</p>

                        <h4 className="nes-text is-warning">Game Rules:</h4>
                        <ul className="nes-list is-disc">
                            <li>• Players alternate between sending insults and responses</li>
                            <li>• Each round consists of one insult and one comeback</li>
                            <li>• The AI judges each exchange and determines the winner</li>
                            <li>• The losing player loses one health point</li>
                            <li>• First player to lose all health points loses the battle</li>
                        </ul>

                        <h4 className="nes-text is-warning">Controls:</h4>
                        <ul className="nes-list is-disc">
                            <li>• Use the "Insult" button to start your attack</li>
                            <li>• Use the "Respond" button to counter your opponent's insult</li>
                            <li>• Click "See Results" to view the AI's judgment</li>
                            <li>• Use "Next Round" to continue the battle</li>
                            <li>• Access the Pirate Dictionary for inspiration and pirate terms</li>
                        </ul>

                        <h4 className="nes-text is-warning">Example Round:</h4>
                        <div className="example-round">
                            <p><strong>Player 1 Insult:</strong>
                                "Ye be uglier than a barnacle on the bottom of me ship!"</p>

                            <p><strong>Player 2 Comeback:</strong>
                                "Aye, and ye be the reason barnacles grow there - they're tryin' to hide from yer face!"</p>

                            <p><strong>Result:</strong> Player 2 wins this round!</p>

                            <p><strong>Reason:</strong>
                                The comeback cleverly built upon the barnacle theme while escalating the insult.</p>
                        </div>



                        <h4 className="nes-text is-warning">Tips:</h4>
                        <ul className="nes-list is-disc">
                            <li>• Use the Pirate Dictionary to learn authentic pirate phrases</li>
                            <li>• Be creative - the AI rewards originality and humor</li>
                            <li>• Keep your insults and comebacks appropriate</li>
                            <li>• Try to maintain a consistent pirate theme</li>
                        </ul>

                        <p><span className="nes-text is-success">Remember:</span>
                            The best insults are clever, quick, and leave your opponent speechless!</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HowToPlay;
